import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Target, Briefcase, Sparkles, Clock, TrendingUp, Trophy, Users,
  Mail, Send, Save, RotateCcw, FileCode, MessageSquare, FileDown, Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NovaDashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [tab, setTab] = useState("kpis");
  const [filters, setFilters] = useState({ campaign_id: "", period: "all", area: "" });

  const loadStats = (campaign_id = filters.campaign_id, period = filters.period, area = filters.area) => {
    const params = new URLSearchParams();
    if (campaign_id) params.set("campaign_id", campaign_id);
    if (period && period !== "all") params.set("period", period);
    if (area) params.set("area", area);
    fetch(`${BACKEND_URL}/api/nova/stats?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setStats);
    // Leaderboard doesn't use area (only campaign+period)
    const lbParams = new URLSearchParams();
    if (campaign_id) lbParams.set("campaign_id", campaign_id);
    if (period && period !== "all") lbParams.set("period", period);
    fetch(`${BACKEND_URL}/api/nova/leaderboard?${lbParams}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setLeaderboard);
  };

  useEffect(() => {
    if (!token) return;
    loadStats();
  }, [token]); // eslint-disable-line

  const applyFilter = (next) => {
    setFilters(next);
    loadStats(next.campaign_id, next.period, next.area);
  };

  const exportPdf = () => {
    const params = new URLSearchParams();
    if (filters.campaign_id) params.set("campaign_id", filters.campaign_id);
    if (filters.period && filters.period !== "all") params.set("period", filters.period);
    if (filters.area) params.set("area", filters.area);
    const tab = window.open("", "_blank");
    fetch(`${BACKEND_URL}/api/nova/stats/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { if (tab) tab.location = URL.createObjectURL(b); });
  };

  if (!stats || !leaderboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"/>
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="nova-dashboard">
      <button onClick={() => navigate("/nova")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
        <ArrowLeft className="w-4 h-4"/> NOVA
      </button>

      <div
        className="rounded-3xl p-8 relative overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(59,173,45,0.25), transparent 45%), radial-gradient(circle at 90% 80%, rgba(139,92,246,0.25), transparent 50%), linear-gradient(135deg, #04140a 0%, #0b1f12 50%, #04140a 100%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-emerald-300 text-xs uppercase tracking-[0.3em] font-medium">Innovation Radar · Executive Dashboard</div>
            <h1 className="text-4xl md:text-5xl font-black text-white mt-2 tracking-tight">Dashboard NOVA</h1>
            <p className="text-emerald-100/80 mt-2 text-sm max-w-2xl">KPIs históricos de conversión, calidad de matches IA, leaderboard de champions y configuración del digest semanal.</p>
          </div>
          <Button onClick={exportPdf} data-testid="export-dashboard-pdf" className="bg-white text-emerald-900 hover:bg-emerald-50 gap-2 rounded-full font-semibold">
            <FileDown className="w-4 h-4"/> Export PDF ejecutivo
          </Button>
        </div>
        {/* Filters (visible in kpis + leaderboard tabs) */}
        {(tab === "kpis" || tab === "leaderboard") && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-emerald-200"/>
            <select
              value={filters.campaign_id}
              onChange={(e) => applyFilter({ ...filters, campaign_id: e.target.value })}
              className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white border border-white/20 outline-none"
              data-testid="dash-filter-campaign"
            >
              <option value="" style={{color:"#000"}}>Todas las campañas</option>
              {(stats.filters?.available_campaigns || []).map((c) => (
                <option key={c.id} value={c.id} style={{color:"#000"}}>{c.area} · {c.name.slice(0, 40)}…</option>
              ))}
            </select>
            <div className="flex bg-white/10 rounded-full p-1 border border-white/20">
              {[
                { v: "all", label: "Todos" },
                { v: "12m", label: "12m" },
                { v: "6m", label: "6m" },
                { v: "3m", label: "3m" },
              ].map((p) => (
                <button
                  key={p.v}
                  onClick={() => applyFilter({ ...filters, period: p.v })}
                  data-testid={`dash-filter-period-${p.v}`}
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${filters.period === p.v ? "bg-white text-emerald-900 font-bold" : "text-white/70 hover:text-white"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {(filters.campaign_id || filters.period !== "all" || filters.area) && (
              <button onClick={() => applyFilter({ campaign_id: "", period: "all", area: "" })} className="text-xs text-white/60 hover:text-white" data-testid="dash-filter-clear">Limpiar</button>
            )}
            {filters.area && (
              <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-100 border border-emerald-300/30 flex items-center gap-2" data-testid="active-area-chip">
                Área: <strong>{filters.area}</strong>
                <button onClick={() => applyFilter({ ...filters, area: "" })} className="hover:text-white" aria-label="Quitar filtro de área">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-4 overflow-x-auto">
        {[
          { id: "kpis", label: "KPIs ejecutivos", icon: TrendingUp },
          { id: "leaderboard", label: "Champions Leaderboard", icon: Trophy },
          { id: "digest", label: "Plantilla Digest", icon: Mail },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
              className={`pb-2 px-1 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${tab===t.id?"border-emerald-500 text-foreground":"border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon className="w-4 h-4"/>{t.label}
            </button>
          );
        })}
      </div>

      {tab === "kpis" && <KpisView stats={stats} onAreaClick={(a) => applyFilter({ ...filters, area: a })} onCampaignClick={(cid) => applyFilter({ ...filters, campaign_id: cid })} activeArea={filters.area} activeCampaign={filters.campaign_id}/>}
      {tab === "leaderboard" && <LeaderboardView leaderboard={leaderboard}/>}
      {tab === "digest" && <DigestEditor token={token}/>}
    </div>
  );
}

function KpisView({ stats, onAreaClick, onCampaignClick, activeArea, activeCampaign }) {
  const { totals, kpis, by_area, by_campaign, sources, monthly } = stats;
  const maxArea = Math.max(...by_area.map(a => a.challenges), 1);
  const maxCamp = Math.max(...by_campaign.map(c => c.challenges), 1);

  return (
    <div className="space-y-7">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Retos totales" value={totals.challenges} icon={<Target/>} accent="emerald" sub={`${totals.campaigns} campañas`}/>
        <Kpi label="Conversión a portfolio" value={`${kpis.conversion_rate}%`} icon={<Briefcase/>} accent="violet" sub={`${totals.in_portfolio} de ${totals.challenges}`}/>
        <Kpi label="Matches IA alta calidad" value={`${kpis.pct_high_quality_matches}%`} icon={<Sparkles/>} accent="amber" sub={`Score medio top: ${kpis.avg_top_match_score}`}/>
        <Kpi label="Time-to-portfolio" value={kpis.avg_time_to_portfolio_days != null ? `${kpis.avg_time_to_portfolio_days}d` : "—"} icon={<Clock/>} accent="rose" sub={`${totals.matched} retos matcheados`}/>
      </div>

      {/* Monthly evolution */}
      {monthly && monthly.length > 0 && (
        <Card title="Evolución mensual" subtitle={`Últimos ${monthly.length} meses`}>
          <MonthlyChart data={monthly}/>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
            <LegendItem color="#0b3b22" label="Retos creados"/>
            <LegendItem color="#3BAD2D" label="A portfolio"/>
            <LegendItem color="#a78bfa" label="Respuestas públicas" dashed/>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By area */}
        <Card title="Retos por área" subtitle={`Top ${by_area.length} áreas · click para drill-down`}>
          <div className="space-y-2">
            {by_area.map(a => {
              const isActive = activeArea === a.area;
              return (
                <button
                  key={a.area}
                  onClick={() => onAreaClick && onAreaClick(isActive ? "" : a.area)}
                  data-testid={`area-bar-${a.area.replace(/[^a-z0-9]/gi,'-')}`}
                  className={`w-full text-left space-y-1 rounded-lg p-2 transition-colors -mx-2 ${isActive ? "bg-emerald-500/15 ring-1 ring-emerald-500/40" : "hover:bg-muted/40"}`}
                  title="Click para filtrar el dashboard por esta área"
                >
                  <div className="flex justify-between text-xs">
                    <span className="font-medium truncate max-w-[60%]">{a.area} {isActive && "✓"}</span>
                    <span className="text-muted-foreground">{a.challenges} retos · {a.in_portfolio} en portfolio · {a.high_priority} alta</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                    <div className="bg-emerald-500" style={{ width: `${(a.in_portfolio / maxArea) * 100}%` }}/>
                    <div className="bg-emerald-500/40" style={{ width: `${((a.matched - a.in_portfolio) / maxArea) * 100}%` }}/>
                    <div className="bg-muted-foreground/15" style={{ width: `${((a.challenges - a.matched) / maxArea) * 100}%` }}/>
                  </div>
                </button>
              );
            })}
          </div>
          <Legend/>
        </Card>

        {/* By campaign */}
        <Card title="Estado por campaña" subtitle={`${by_campaign.length} campañas · click para drill-down`}>
          <div className="space-y-3">
            {by_campaign.map(c => {
              const isActive = activeCampaign === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => onCampaignClick && onCampaignClick(isActive ? "" : c.id)}
                  data-testid={`campaign-bar-${c.id.slice(0,8)}`}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${isActive ? "border-emerald-500/60 bg-emerald-500/10" : "border-border hover:bg-muted/40"}`}
                  title="Click para filtrar el dashboard por esta campaña"
                >
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold truncate max-w-[55%]">{c.area} {isActive && "✓"}</span>
                    <span className="text-xs text-muted-foreground">{c.challenges} retos · {c.submissions} respuestas</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden flex">
                    <div className="bg-violet-500" style={{ width: `${(c.in_portfolio / maxCamp) * 100}%` }}/>
                    <div className="bg-amber-500" style={{ width: `${((c.matched - c.in_portfolio) / maxCamp) * 100}%` }}/>
                    <div className="bg-muted-foreground/15" style={{ width: `${((c.challenges - c.matched) / maxCamp) * 100}%` }}/>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">{c.in_portfolio} en portfolio · {c.matched} con match IA · Conv. <strong className="text-foreground">{c.conversion}%</strong></div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Sources mix */}
      <Card title="Origen de los retos" subtitle="Cómo llegan los retos a NOVA">
        <div className="grid grid-cols-3 gap-3">
          <SourceCard label="Sesión Miro" count={sources.miro || 0} total={totals.challenges} color="#3BAD2D"/>
          <SourceCard label="Manual / Innovation" count={sources.manual || 0} total={totals.challenges} color="#a78bfa"/>
          <SourceCard label="Formulario público" count={sources.public_form || 0} total={totals.challenges} color="#fbbf24"/>
        </div>
      </Card>
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"/>En portfolio</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/40"/>Con match IA</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-muted-foreground/15"/>Sin match</span>
    </div>
  );
}

function LegendItem({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-4 h-[3px] rounded-sm" style={{
        background: dashed ? `repeating-linear-gradient(90deg, ${color} 0 4px, transparent 4px 7px)` : color
      }}/>
      {label}
    </span>
  );
}

function MonthlyChart({ data }) {
  const W = 720, H = 220;
  const padL = 36, padR = 10, padT = 12, padB = 24;
  const n = data.length;
  const chs = data.map(d => d.challenges);
  const ports = data.map(d => d.to_portfolio);
  const subs = data.map(d => d.submissions || 0);
  const max = Math.max(1, ...chs, ...ports, ...subs);
  const stepX = (W - padL - padR) / Math.max(n - 1, 1);
  const pt = (i, v) => [padL + i * stepX, H - padB - (v / max) * (H - padT - padB)];
  const path = (series) => "M " + series.map((v, i) => pt(i, v).join(",")).join(" L ");
  const dots = (series, color) => series.map((v, i) => {
    const [x, y] = pt(i, v);
    return <circle key={i} cx={x} cy={y} r={3} fill={color}/>;
  });
  const gridLines = Array.from({ length: 5 }, (_, k) => {
    const y = padT + k * (H - padT - padB) / 4;
    const v = Math.round(max * (1 - k / 4));
    return (
      <g key={k}>
        <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="currentColor" strokeOpacity={0.08} strokeDasharray="2 4"/>
        <text x={padL - 5} y={y + 3} fontSize={9} className="fill-muted-foreground" textAnchor="end">{v}</text>
      </g>
    );
  });

  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // {i, x, y}
  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const sx = ((e.clientX - rect.left) / rect.width) * W;
    // nearest index
    const i = Math.round((sx - padL) / stepX);
    const idx = Math.max(0, Math.min(n - 1, i));
    const [x] = pt(idx, 0);
    setHover({ i: idx, x });
  };
  const onLeave = () => setHover(null);

  const tip = hover ? data[hover.i] : null;
  const tipLeft = hover ? `${(hover.x / W) * 100}%` : 0;
  // tooltip positioning: prefer left if cursor on right half
  const tipFlip = hover && hover.x > W * 0.7;

  return (
    <div className="w-full overflow-x-auto relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full"
        onMouseMove={onMove} onMouseLeave={onLeave}
        style={{ minWidth: 540, height: 220, cursor: "crosshair" }}
        data-testid="monthly-chart">
        {gridLines}
        <path d={path(chs)} fill="none" stroke="#0b3b22" strokeWidth={2.4} strokeLinecap="round"/>
        <path d={path(ports)} fill="none" stroke="#3BAD2D" strokeWidth={2.4} strokeLinecap="round"/>
        <path d={path(subs)} fill="none" stroke="#a78bfa" strokeWidth={2.4} strokeLinecap="round" strokeDasharray="5 4"/>
        {dots(chs, "#0b3b22")}{dots(ports, "#3BAD2D")}{dots(subs, "#a78bfa")}
        {hover && <line x1={hover.x} y1={padT} x2={hover.x} y2={H - padB} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth={1}/>}
        {hover && [chs, ports, subs].map((s, k) => {
          const colors = ["#0b3b22","#3BAD2D","#a78bfa"];
          const [x, y] = pt(hover.i, s[hover.i]);
          return <circle key={k} cx={x} cy={y} r={5} fill="#fff" stroke={colors[k]} strokeWidth={2}/>;
        })}
        {data.map((d, i) => {
          const [x] = pt(i, 0);
          return <text key={i} x={x} y={H - 6} fontSize={9} className="fill-muted-foreground" textAnchor="middle">{d.month.slice(2)}</text>;
        })}
      </svg>
      {tip && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap z-10"
          style={{ left: tipFlip ? "auto" : tipLeft, right: tipFlip ? `${100 - (hover.x / W) * 100}%` : "auto", top: 8, transform: tipFlip ? "translate(0,0)" : "translateX(-50%)" }}
        >
          <div className="font-bold text-foreground mb-1">{tip.month}</div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#0b3b22"}}/>Creados: <strong>{tip.challenges}</strong></div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#3BAD2D"}}/>A portfolio: <strong>{tip.to_portfolio}</strong></div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:"#a78bfa"}}/>Respuestas: <strong>{tip.submissions || 0}</strong></div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, accent = "emerald", sub }) {
  const colors = {
    emerald: { bg: "from-emerald-500/15 to-transparent", text: "text-emerald-600 dark:text-emerald-400" },
    violet: { bg: "from-violet-500/15 to-transparent", text: "text-violet-600 dark:text-violet-400" },
    amber: { bg: "from-amber-500/15 to-transparent", text: "text-amber-600 dark:text-amber-400" },
    rose: { bg: "from-rose-500/15 to-transparent", text: "text-rose-600 dark:text-rose-400" },
  }[accent];
  return (
    <div className={`rounded-2xl border border-border bg-gradient-to-br ${colors.bg} p-4`}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${colors.text}`}>
        <span className="w-3.5 h-3.5">{icon}</span>
        {label}
      </div>
      <div className="text-3xl font-black mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-base font-bold">{title}</h3>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function SourceCard({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rounded-xl border border-border p-3 text-center">
      <div className="text-3xl font-black" style={{ color }}>{count}</div>
      <div className="text-xs uppercase tracking-wider font-semibold mt-1 text-muted-foreground">{label}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{pct}% del total</div>
    </div>
  );
}

function LeaderboardView({ leaderboard }) {
  const { token } = useAuth();
  const [cotm, setCotm] = useState(null);
  const [history, setHistory] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nova/champion-of-month`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setCotm).catch(() => {});
    fetch(`${BACKEND_URL}/api/nova/champions-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setHistory(d.history || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/nova/celebrations?limit=12`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setCelebrations(d.celebrations || [])).catch(() => {});
  }, [token]);
  const sendCotm = async () => {
    if (!window.confirm("¿Enviar email + Teams del Champion of the Month al equipo de innovación?")) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/champion-of-month/send`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ teams: true })
      });
      const d = await r.json();
      if (r.ok) toast.success(`Champion enviado: ${d.champion} · Teams: ${d.teams_sent ? "sí" : "no"}`);
      else toast.error(d.detail || "Sin actividad el mes pasado");
    } finally { setSending(false); }
  };

  const podium = leaderboard.champions.slice(0, 3);
  const rest = leaderboard.champions.slice(3);

  return (
    <div className="space-y-6">
      {/* Champion of the Month */}
      {cotm?.champion ? (
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} className="relative rounded-2xl overflow-hidden p-6 text-white" style={{ background: "linear-gradient(135deg,#7c2d12 0%,#b45309 50%,#fbbf24 100%)" }}>
          <div className="absolute right-3 top-3 text-7xl opacity-25">🏆</div>
          <div className="relative">
            <div className="text-amber-100/90 text-xs uppercase tracking-[0.3em] font-bold">Champion of the Month · {cotm.champion.month_label}</div>
            <h3 className="text-3xl md:text-4xl font-black mt-2">{cotm.champion.name}</h3>
            <div className="mt-1 text-amber-100/90 text-sm">Score {cotm.champion.score} · {cotm.champion.challenges} retos · {cotm.champion.in_portfolio} en portfolio</div>
            <Button onClick={sendCotm} disabled={sending} data-testid="cotm-send"
              className="mt-4 bg-white text-amber-900 hover:bg-amber-50 gap-2 rounded-full">
              <Send className="w-4 h-4"/> Enviar Champion email + Teams
            </Button>
          </div>
        </motion.div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground flex items-center justify-between gap-3">
          <div>🏆 <strong>Champion of the Month</strong> — aún sin actividad el mes pasado. Se enviará automáticamente el día 1 a las 09:00 UTC.</div>
          <Button variant="outline" size="sm" onClick={sendCotm} disabled={sending} data-testid="cotm-send-empty">Probar envío</Button>
        </div>
      )}

      {/* History gallery */}
      {history.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-base font-bold">🏆 Galería de Champions</h3>
            <span className="text-xs text-muted-foreground">{history.length} mes{history.length === 1 ? "" : "es"} en el hall of fame</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {history.map((h) => (
              <motion.div
                key={h.month_label}
                whileHover={{ y: -3 }}
                className="rounded-xl p-4 text-white relative overflow-hidden"
                style={{ background: "linear-gradient(135deg,#7c2d12,#b45309 60%,#fbbf24)" }}
                data-testid={`history-${h.month_label.replace(/\s/g, '-')}`}
              >
                <div className="absolute -right-2 -top-2 text-4xl opacity-25">🏆</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-100/90">{h.month_label}</div>
                <div className="text-base font-black mt-1 leading-tight">{h.name}</div>
                <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
                  <div><span className="opacity-70">Score</span><div className="font-bold text-sm">{h.score}</div></div>
                  <div><span className="opacity-70">Retos</span><div className="font-bold text-sm">{h.challenges}</div></div>
                  <div><span className="opacity-70">Portf.</span><div className="font-bold text-sm">{h.in_portfolio}</div></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Celebrations timeline */}
      {celebrations.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-base font-bold">🎉 Timeline de celebraciones</h3>
            <span className="text-xs text-muted-foreground">Retos NOVA llevados a portfolio</span>
          </div>
          <ol className="relative border-l-2 border-emerald-500/30 ml-2 space-y-4">
            {celebrations.map((c, i) => (
              <motion.li
                key={c.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
                className="pl-5 relative"
                data-testid={`celebration-${c.id.slice(0,8)}`}
              >
                <span className="absolute -left-[10px] top-1 w-4 h-4 rounded-full bg-emerald-500 ring-4 ring-emerald-500/15 flex items-center justify-center text-[10px]">🎉</span>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  {new Date(c.celebrated_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                  {c.area && <> · {c.area}</>}
                </div>
                <div className="text-sm font-semibold mt-0.5">
                  <span className="text-muted-foreground line-through decoration-emerald-500/40">«{c.challenge_title.slice(0, 80)}…»</span>
                </div>
                <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  ↳ {c.initiative_name.slice(0, 90)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Champion: {c.champion || "—"} · {c.votes} votos · creada por {c.created_by_name}
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      )}

      {/* Podium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {podium.map((c, i) => (
          <motion.div
            key={c.name}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className={`rounded-2xl p-5 relative overflow-hidden ${
              i === 0
                ? "bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-500/30"
                : i === 1
                ? "bg-gradient-to-br from-slate-400/20 to-slate-600/10 border border-slate-500/30"
                : "bg-gradient-to-br from-orange-700/20 to-orange-900/10 border border-orange-700/30"
            }`}
            data-testid={`champion-podium-${i}`}
          >
            <div className="text-5xl absolute -top-2 -right-2 opacity-30">
              {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}
            </div>
            <div className="text-xs uppercase tracking-widest font-bold opacity-80">{c.tier}</div>
            <h3 className="text-lg font-black mt-1 leading-tight">{c.name}</h3>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Stat label="Score" value={c.score}/>
              <Stat label="Retos" value={c.challenges}/>
              <Stat label="En portfolio" value={c.in_portfolio}/>
              <Stat label="Votos" value={c.votes}/>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Full table */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Champion</th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2 text-right">Retos</th>
              <th className="px-3 py-2 text-right">Alta</th>
              <th className="px-3 py-2 text-right">Match</th>
              <th className="px-3 py-2 text-right">Portfolio</th>
              <th className="px-3 py-2 text-right">Votos</th>
              <th className="px-3 py-2 text-left">Áreas</th>
            </tr>
          </thead>
          <tbody>
            {rest.map((c) => (
              <tr key={c.name} className="border-t border-border hover:bg-muted/20">
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.rank}</td>
                <td className="px-3 py-2 font-semibold">{c.name}<div className="text-[10px] text-muted-foreground font-normal">{c.tier}</div></td>
                <td className="px-3 py-2 text-right font-mono font-bold">{c.score}</td>
                <td className="px-3 py-2 text-right">{c.challenges}</td>
                <td className="px-3 py-2 text-right">{c.high_priority}</td>
                <td className="px-3 py-2 text-right">{c.matched}</td>
                <td className="px-3 py-2 text-right">
                  {c.in_portfolio > 0 ? <span className="text-emerald-600 font-semibold">{c.in_portfolio}</span> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-right">{c.votes}</td>
                <td className="px-3 py-2 text-[10px] text-muted-foreground">{(c.areas || []).slice(0, 2).join(", ")}{c.areas?.length > 2 && ` +${c.areas.length - 2}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted-foreground text-center">
        Score = 5pt · iniciativa creada + 3pt · reto prioridad alta + 2pt · match IA + 2pt · respuesta pública + 1pt · reto + 0.1pt · voto
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-black/10 px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  );
}

function DigestEditor({ token }) {
  const [tpl, setTpl] = useState(null);
  const [vars, setVars] = useState({});
  const [html, setHtml] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [sendToTeams, setSendToTeams] = useState(true);
  const iframeRef = useRef(null);

  const loadAll = async () => {
    const [t, p] = await Promise.all([
      fetch(`${BACKEND_URL}/api/nova/digest/template`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      fetch(`${BACKEND_URL}/api/nova/digest/preview`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    ]);
    setTpl(t);
    setVars(t.vars);
    setHtml(t.html);
    setPreviewHtml(p.html);
    setStats(p.stats);
  };
  useEffect(() => { loadAll(); }, [token]); // eslint-disable-line

  useEffect(() => {
    if (previewHtml && iframeRef.current) {
      const d = iframeRef.current.contentDocument;
      d.open(); d.write(previewHtml); d.close();
    }
  }, [previewHtml]);

  const save = async () => {
    const r = await fetch(`${BACKEND_URL}/api/nova/digest/template`, {
      method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ html, vars }),
    });
    if (r.ok) { toast.success("Plantilla guardada"); refreshPreview(); }
    else toast.error("Error al guardar");
  };
  const reset = async () => {
    if (!window.confirm("¿Restaurar plantilla por defecto?")) return;
    await fetch(`${BACKEND_URL}/api/nova/digest/template/reset`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    loadAll();
    toast.success("Plantilla restaurada");
  };
  const refreshPreview = async () => {
    const p = await fetch(`${BACKEND_URL}/api/nova/digest/preview`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json());
    setPreviewHtml(p.html);
  };
  const sendNow = async () => {
    if (!window.confirm("¿Enviar el digest ahora?")) return;
    setSending(true);
    try {
      const to = recipients.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await fetch(`${BACKEND_URL}/api/nova/digest/send`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: to.length ? to : null, teams: sendToTeams }),
      });
      const d = await r.json();
      if (r.ok) toast.success(`Digest enviado (Teams: ${d.teams_sent ? "sí" : "no"})`);
      else toast.error(d.detail || "Error");
    } finally { setSending(false); }
  };

  if (!tpl) return <div className="text-muted-foreground text-sm">Cargando plantilla…</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_640px] gap-5">
      <div className="space-y-4">
        {/* Vars */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-base font-bold mb-3 flex items-center gap-2"><FileCode className="w-4 h-4"/> Variables</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["TITLE", "SUBTITLE", "INTRO", "CTA", "FOOTER"].map((k) => (
              <label key={k} className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{k}</span>
                <input
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  value={vars[k] || ""}
                  onChange={(e) => setVars({ ...vars, [k]: e.target.value })}
                  data-testid={`tpl-var-${k}`}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground">
            Variables disponibles en el HTML: <code>{`{{TITLE}}, {{SUBTITLE}}, {{INTRO}}, {{DATE}}, {{CTA}}, {{FOOTER}}, {{SUBMISSIONS_COUNT}}, {{MATCHES_COUNT}}, {{ORPHANS_COUNT}}, {{SUBMISSIONS_TABLE}}, {{MATCHES_TABLE}}, {{ORPHANS_TABLE}}`}</code>
          </div>
        </div>

        {/* HTML editor */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-base font-bold mb-3 flex items-center gap-2"><Mail className="w-4 h-4"/> HTML de la plantilla</h3>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            data-testid="tpl-html"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs font-mono"
            rows={16}
            spellCheck={false}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="tpl-save"><Save className="w-4 h-4"/> Guardar plantilla</Button>
            <Button variant="outline" onClick={refreshPreview} className="gap-2"><RotateCcw className="w-4 h-4"/> Refrescar preview</Button>
            <Button variant="ghost" onClick={reset} className="gap-2 text-destructive">Restaurar default</Button>
          </div>
        </div>

        {/* Send now */}
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
          <h3 className="text-base font-bold mb-2 flex items-center gap-2"><Send className="w-4 h-4"/> Enviar digest ahora</h3>
          <p className="text-xs text-muted-foreground mb-3">El scheduler corre cada lunes 08:30 UTC. Aquí puedes lanzarlo manualmente para probar.</p>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Destinatarios (coma · vacío = innovation@valorizasm.com)</span>
            <input
              className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="email1@valorizasm.com, email2@…"
              data-testid="digest-recipients"
            />
          </label>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendToTeams} onChange={(e) => setSendToTeams(e.target.checked)} data-testid="digest-teams"/>
            <MessageSquare className="w-4 h-4 text-violet-500"/> Notificar también al canal de Teams
          </label>
          {stats && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <Snip label="Respuestas 7d" value={stats.submissions}/>
              <Snip label="Matches IA" value={stats.new_matches}/>
              <Snip label="Huérfanos alta" value={stats.orphans}/>
            </div>
          )}
          <Button onClick={sendNow} disabled={sending} className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="digest-send-now">
            <Send className="w-4 h-4"/> {sending ? "Enviando…" : "Enviar digest ahora"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border overflow-hidden bg-slate-100 sticky top-20 self-start">
        <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border bg-card">Vista previa con datos reales</div>
        <iframe ref={iframeRef} title="Digest preview" className="w-full bg-white" style={{ height: "900px", border: 0 }}/>
      </div>
    </div>
  );
}

function Snip({ label, value }) {
  return (
    <div className="rounded-lg border border-border px-2 py-1.5 bg-card text-center">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  );
}
